import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LangProvider } from "@/context/LangContext";
import Layout from "@/components/Layout";
import LoginPage from "@/pages/Login";
import RegisterPage from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import CatalogPage from "@/pages/Catalog";
import ResourceView from "@/pages/ResourceView";
import PoliciesPage from "@/pages/Policies";
import SegmentsPage from "@/pages/Segments";
import SiemPage from "@/pages/SIEM";
import ScenariosPage from "@/pages/Scenarios";
import CompliancePage from "@/pages/Compliance";
import UsersPage from "@/pages/Users";
import DevicesPage from "@/pages/Devices";
import SitesPage from "@/pages/Sites";
import "@/App.css";

const PAGE_TITLES = {
  "/login": "Connexion",
  "/register": "Inscription",
  "/app": "Dashboard",
  "/app/catalog": "Catalogue de ressources",
  "/app/policies": "Politiques de sécurité (PDP)",
  "/app/segments": "Micro-segmentation",
  "/app/siem": "SIEM & Journalisation",
  "/app/scenarios": "Scénarios de validation",
  "/app/compliance": "Conformité RGPD & HIPAA",
  "/app/users": "Gestion des utilisateurs",
  "/app/devices": "Posture des appareils",
  "/app/sites": "Cartographie des sites",
};

function PageTitleUpdater() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname;
    let pageName = PAGE_TITLES[path];
    if (!pageName && path.startsWith("/app/resource/")) {
      pageName = "Consulter la ressource";
    }
    document.title = pageName ? `Zero Trust App - ${pageName}` : "Zero Trust App";
  }, [location]);

  return null;
}

function Protected({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white/40 flex items-center justify-center">
        Chargement…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/app" replace />;
  return <Layout>{children}</Layout>;
}

function AppRoutes() {
  return (
    <>
      <PageTitleUpdater />
      <Routes>
        <Route path="/" element={<Navigate to="/app" replace />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/app" element={<Protected><Dashboard /></Protected>} />
        <Route path="/app/catalog" element={<Protected><CatalogPage /></Protected>} />
        <Route path="/app/resource/:id" element={<Protected><ResourceView /></Protected>} />
        <Route path="/app/policies"
               element={<Protected roles={["admin", "auditeur"]}><PoliciesPage /></Protected>} />
        <Route path="/app/segments"
               element={<Protected roles={["admin", "auditeur"]}><SegmentsPage /></Protected>} />
        <Route path="/app/siem"
               element={<Protected roles={["admin", "auditeur"]}><SiemPage /></Protected>} />
        <Route path="/app/scenarios" element={<Protected><ScenariosPage /></Protected>} />
        <Route path="/app/compliance"
               element={<Protected roles={["admin", "auditeur"]}><CompliancePage /></Protected>} />
        <Route path="/app/users"
               element={<Protected roles={["admin"]}><UsersPage /></Protected>} />
        <Route path="/app/devices" element={<Protected><DevicesPage /></Protected>} />
        <Route path="/app/sites"
               element={<Protected roles={["admin", "auditeur"]}><SitesPage /></Protected>} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster
            theme="dark"
            position="top-right"
            toastOptions={{
              style: {
                background: "#0A0A0A",
                border: "1px solid rgba(255,255,255,0.1)",
                color: "#fff",
                fontFamily: "IBM Plex Sans, sans-serif",
                borderRadius: 2,
              },
            }}
          />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}
